package top.wcpe.allincore.ui.runtime.style

import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

/**
 * 色彩矩阵纯函数工具：把 CSS filter 各原语（brightness / contrast / grayscale /
 * sepia / hue-rotate / saturate / invert）折算为单个 4×5 颜色矩阵并逐像素应用。
 *
 * **矩阵布局**：4×5 共 20 个 float，行优先排列。前 4×4 为线性变换部分，
 * 第 5 列（下标 4/9/14/19）为偏移列（以 0..1 归一化色域计算）：
 * ```
 *   r' = m[0]*r + m[1]*g + m[2]*b + m[3]*a + m[4]
 *   g' = m[5]*r + m[6]*g + m[7]*b + m[8]*a + m[9]
 *   b' = m[10]*r+ m[11]*g+ m[12]*b+ m[13]*a+ m[14]
 *   a' = m[15]*r+ m[16]*g+ m[17]*b+ m[18]*a+ m[19]
 * ```
 * 各分量先归一化到 0..1 参与运算，输出钳制到 0..1 后还原回 0..255。
 *
 * **合成顺序**（约定固定）：
 * brightness → contrast → grayscale → sepia → hue-rotate → saturate → invert。
 * 后乘前：合成 = invert · saturate · hueRotate · sepia · grayscale · contrast · brightness，
 * 即按上述书写顺序依次「左乘」到累积矩阵（先应用 brightness，再应用 contrast……）。
 * 多个色彩矩阵的组合顺序对最终视觉有影响，但对单层 UI 装饰场景影响有限，
 * 此处固定顺序以简化实现与测试。
 *
 * **已知近似边界**：本工具逐图元（fillRect/drawText 等）应用矩阵，属于
 * 「混合前变换」（每个图元在写入帧缓冲前先变色），而 CSS filter 的语义是
 * 「先把元素及其子树合成为一张离屏图，再对整张图做变换」（混合后变换）。
 * 对子树内含多层半透明叠加 / 渐变重叠的情况，两者不完全等价；单层、低重叠
 * 的 UI 装饰可接受。像素级正确需离屏 FBO（E2 范畴，本实现不涉及）。
 *
 * 纯函数、无可变全局状态，可直接单测。
 */
@Suppress("MagicNumber", "TooManyFunctions")
object ColorMatrix {

    /** Rec.709 亮度权重（luma）：grayscale / saturate / hue-rotate 共用。 */
    /** 半圆角度数（角度 → 弧度换算分母）。 */
    private const val HALF_CIRCLE_DEG = 180.0

    /** 颜色通道最大值（8 位）。 */
    private const val MAX_CHANNEL = 255f

    private const val LUMA_R = 0.2126f
    private const val LUMA_G = 0.7152f
    private const val LUMA_B = 0.0722f

    /** 单位矩阵（identity）：线性部分为 4×4 单位阵，偏移列全 0。 */
    @JvmField
    val IDENTITY: FloatArray = identity()

    /** 返回一个新的 4×5 单位矩阵。 */
    @JvmStatic
    fun identity(): FloatArray = floatArrayOf(
        1f, 0f, 0f, 0f, 0f,
        0f, 1f, 0f, 0f, 0f,
        0f, 0f, 1f, 0f, 0f,
        0f, 0f, 0f, 1f, 0f,
    )

    /**
     * 从 [StyleVisual] 的 filter 字段合成单个 4×5 颜色矩阵。
     * 所有字段均为默认值（identity 各原语）时返回的矩阵等价于 [identity]，
     * 逐像素应用零变化（零回归保障）。
     */
    @JvmStatic
    fun fromStyle(v: StyleVisual): FloatArray {
        // 固定合成顺序，逐个左乘到累积矩阵（先应用者写在前）：
        var m = identity()
        m = multiply(brightness(v.filterBrightness()), m)
        m = multiply(contrast(v.filterContrast()), m)
        if (v.filterGrayscale() != 0f) {
            m = multiply(grayscale(v.filterGrayscale()), m)
        }
        if (v.filterSepia() != 0f) {
            m = multiply(sepia(v.filterSepia()), m)
        }
        if (v.filterHueRotateDeg() != 0f) {
            m = multiply(hueRotate(v.filterHueRotateDeg()), m)
        }
        if (v.filterSaturate() != 1f) {
            m = multiply(saturate(v.filterSaturate()), m)
        }
        if (v.filterInvert() != 0f) {
            m = multiply(invert(v.filterInvert()), m)
        }
        return m
    }

    /** 亮度：RGB 三通道对角缩放 amount（1 不变，>1 变亮，<1 变暗），alpha 不变。 */
    @JvmStatic
    fun brightness(amount: Float): FloatArray = floatArrayOf(
        amount, 0f, 0f, 0f, 0f,
        0f, amount, 0f, 0f, 0f,
        0f, 0f, amount, 0f, 0f,
        0f, 0f, 0f, 1f, 0f,
    )

    /**
     * 对比度：以 0.5 灰为中心缩放。amount=1 不变。
     * `c' = (c - 0.5) * amount + 0.5 = c*amount + (0.5 - 0.5*amount)`。
     */
    @JvmStatic
    fun contrast(amount: Float): FloatArray {
        val offset = 0.5f - 0.5f * amount
        return floatArrayOf(
            amount, 0f, 0f, 0f, offset,
            0f, amount, 0f, 0f, offset,
            0f, 0f, amount, 0f, offset,
            0f, 0f, 0f, 1f, 0f,
        )
    }

    /**
     * 灰度：amount=1 完全灰度（等价 saturate(0)），amount=0 不变。
     * 按 CSS filter 规范的标准灰度矩阵在 luma 与 identity 之间插值。
     */
    @JvmStatic
    fun grayscale(amount: Float): FloatArray = saturate(1f - amount)

    /**
     * 饱和度：amount=1 不变，amount=0 完全去色（等价灰度），amount>1 增饱和。
     * CSS filter 规范的标准 saturate 矩阵（基于 Rec.709 luma 权重）。
     */
    @JvmStatic
    fun saturate(s: Float): FloatArray {
        val ir = LUMA_R * (1 - s)
        val ig = LUMA_G * (1 - s)
        val ib = LUMA_B * (1 - s)
        return floatArrayOf(
            ir + s, ig, ib, 0f, 0f,
            ir, ig + s, ib, 0f, 0f,
            ir, ig, ib + s, 0f, 0f,
            0f, 0f, 0f, 1f, 0f,
        )
    }

    /**
     * 棕褐色（sepia）：amount=1 完全 sepia，amount=0 不变。
     * CSS filter 规范的标准 sepia 矩阵在 identity 与固定 sepia 矩阵之间插值。
     */
    @JvmStatic
    fun sepia(amount: Float): FloatArray {
        val a = amount
        val m00 = lerp(1f, 0.393f, a)
        val m01 = lerp(0f, 0.769f, a)
        val m02 = lerp(0f, 0.189f, a)
        val m10 = lerp(0f, 0.349f, a)
        val m11 = lerp(1f, 0.686f, a)
        val m12 = lerp(0f, 0.168f, a)
        val m20 = lerp(0f, 0.272f, a)
        val m21 = lerp(0f, 0.534f, a)
        val m22 = lerp(1f, 0.131f, a)
        return floatArrayOf(
            m00, m01, m02, 0f, 0f,
            m10, m11, m12, 0f, 0f,
            m20, m21, m22, 0f, 0f,
            0f, 0f, 0f, 1f, 0f,
        )
    }

    /**
     * 色相旋转：绕亮度轴旋转 degrees 度。0 度不变。
     * CSS filter 规范的标准 hue-rotate 矩阵：luma 权重 + cos/sin 加权。
     */
    @JvmStatic
    fun hueRotate(degrees: Float): FloatArray {
        val rad = degrees.toDouble() * PI / HALF_CIRCLE_DEG
        val cos = cos(rad).toFloat()
        val sin = sin(rad).toFloat()
        val m00 = LUMA_R + cos * (1 - LUMA_R) + sin * (-LUMA_R)
        val m01 = LUMA_G + cos * (-LUMA_G) + sin * (-LUMA_G)
        val m02 = LUMA_B + cos * (-LUMA_B) + sin * (1 - LUMA_B)
        val m10 = LUMA_R + cos * (-LUMA_R) + sin * 0.143f
        val m11 = LUMA_G + cos * (1 - LUMA_G) + sin * 0.140f
        val m12 = LUMA_B + cos * (-LUMA_B) + sin * (-0.283f)
        val m20 = LUMA_R + cos * (-LUMA_R) + sin * (-(1 - LUMA_R))
        val m21 = LUMA_G + cos * (-LUMA_G) + sin * LUMA_G
        val m22 = LUMA_B + cos * (1 - LUMA_B) + sin * LUMA_B
        return floatArrayOf(
            m00, m01, m02, 0f, 0f,
            m10, m11, m12, 0f, 0f,
            m20, m21, m22, 0f, 0f,
            0f, 0f, 0f, 1f, 0f,
        )
    }

    /**
     * 反相：amount=1 完全反相（c' = 1 - c），amount=0 不变，alpha 不变。
     * `c' = (1 - 2a)*c + a`。
     */
    @JvmStatic
    fun invert(amount: Float): FloatArray {
        val scale = 1f - 2f * amount
        return floatArrayOf(
            scale, 0f, 0f, 0f, amount,
            0f, scale, 0f, 0f, amount,
            0f, 0f, scale, 0f, amount,
            0f, 0f, 0f, 1f, 0f,
        )
    }

    /**
     * 把一个 ARGB 像素应用 4×5 颜色矩阵，返回新 ARGB。
     * RGB 归一化到 0..1 参与矩阵运算（alpha 也归一化参与线性部分，但偏移列以
     * 0..1 计），输出钳制到 0..1 后还原回 0..255。alpha 默认行为：identity 矩阵下
     * 输出与输入逐位一致。
     */
    @JvmStatic
    fun applyToArgb(argb: Int, m: FloatArray): Int {
        val a = ((argb ushr 24) and 0xFF) / 255f
        val r = ((argb ushr 16) and 0xFF) / 255f
        val g = ((argb ushr 8) and 0xFF) / 255f
        val b = (argb and 0xFF) / 255f
        val nr = m[0] * r + m[1] * g + m[2] * b + m[3] * a + m[4]
        val ng = m[5] * r + m[6] * g + m[7] * b + m[8] * a + m[9]
        val nb = m[10] * r + m[11] * g + m[12] * b + m[13] * a + m[14]
        val na = m[15] * r + m[16] * g + m[17] * b + m[18] * a + m[19]
        val ri = clamp255(nr)
        val gi = clamp255(ng)
        val bi = clamp255(nb)
        val ai = clamp255(na)
        return (ai shl 24) or (ri shl 16) or (gi shl 8) or bi
    }

    /**
     * 4×5 矩阵乘法：返回 left · right（先应用 right 再应用 left）。
     * 把 4×5 视作齐次 5×5（末行 0,0,0,0,1）做乘法，取前 4 行。
     */
    @JvmStatic
    fun multiply(left: FloatArray, right: FloatArray): FloatArray {
        val out = FloatArray(20)
        for (row in 0 until 4) {
            for (col in 0 until 5) {
                var sum = 0f
                for (k in 0 until 4) {
                    sum += left[row * 5 + k] * right[k * 5 + col]
                }
                if (col == 4) {
                    sum += left[row * 5 + 4]
                }
                out[row * 5 + col] = sum
            }
        }
        return out
    }

    private fun lerp(from: Float, to: Float, t: Float): Float = from + (to - from) * t

    private fun clamp255(v: Float): Int {
        val i = (v * MAX_CHANNEL).roundToInt()
        return when {
            i < 0 -> 0
            i > 255 -> 255
            else -> i
        }
    }
}
